/**
 * Fonctions utilitaires pour les appels au worker wab-config
 */

import { routedFetch } from './api-router.js';

/**
 * Wrapper générique pour les appels API de configuration
 * Utilise le routeur centralisé
 */
export async function fetchConfigApi(endpoint, options = {}) {
  return routedFetch(endpoint, options);
}

/**
 * Récupère les traductions pour une langue et une page donnée
 * @param {string} language - Code de la langue
 * @param {string} page - Nom de la page
 * @returns {Promise<Object>} Traductions
 */
export async function getTranslations(language, page) {
  return fetchConfigApi(`/api/config/read/translations?languageCode=${language}&page=${page}`);
}

/**
 * Récupère la liste des langues disponibles
 * @returns {Promise<Object>} Liste des langues
 */
export async function getLanguages() {
  return fetchConfigApi(`/api/config/read/languages`);
}

/**
 * Récupère la liste des modèles IA avec filtres optionnels
 * @param {Object} filters - Filtres pour les modèles
 * @param {boolean} filters.hasText - Modèles supportant le texte
 * @param {boolean} filters.hasVision - Modèles supportant la vision
 * @param {boolean} filters.hasTranslation - Modèles supportant la traduction
 * @returns {Promise<Object>} Liste des modèles IA
 */
export async function getIAModels(filters = {}) {
  const queryParams = new URLSearchParams();
  
  if (filters.hasText) queryParams.set('hasText', '1');
  if (filters.hasVision) queryParams.set('hasVision', '1');
  if (filters.hasTranslation) queryParams.set('hasTranslation', '1');
  
  return fetchConfigApi(`/api/config/read/models?${queryParams.toString()}`);
}

/**
 * Récupère les langues supportées par un modèle IA spécifique
 * @param {string} modelKey - Clé du modèle IA
 * @returns {Promise<Object>} Langues supportées par le modèle
 */
export async function getModelLanguages(modelKey) {
  if (!modelKey) {
    throw new Error('Model key is required');
  }
  return fetchConfigApi(`/api/config/read/models/${modelKey}/languages`);
}

/**
 * Check if the current API key is a master key
 * La clé API est déjà incluse dans le header X-Routing-Data par routedFetch
 * NE PAS passer de paramètre apiKey - elle est automatiquement envoyée
 * Returns a response with isMaster boolean and toolUiRoutes array
 */
export async function checkIsMaster() {
  return fetchConfigApi(`/api/config/is-master`);
}

/**
 * Récupère les templates Handlebars compilés pour une langue et des types de source
 * Utilise le routeur centralisé pour l'authentification et la gestion du cache
 * @param {string} iaLanguage - Code de la langue (ex: 'FR', 'EN')
 * @param {string|string[]} sourceTypes - Type(s) de source de templates
 * @returns {Promise<Object>} Response avec compiled templates
 */
export async function getCompiledTemplates(iaLanguage, sourceTypes) {
  const sourceTypesList = Array.isArray(sourceTypes) ? sourceTypes.join(',') : sourceTypes;
  return fetchConfigApi(`/api/config/read/hb-compiled?ia_language=${iaLanguage}&source_types=${sourceTypesList}`);
}
